import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { ApiError, api } from './api';
import type { Channel, ConversationDetail } from '../types';

export interface StartConversationInput {
  contactId: string;
  channel: Channel;
  team?: string;
}

/**
 * Eröffnet (oder findet) eine Conversation für einen Kontakt + Kanal und
 * navigiert in den Posteingang zur Conversation.
 *
 * POST /api/conversations gibt 200 (bestehende) oder 201 (neue) zurück — in
 * beiden Fällen die volle ConversationDetail. Wir cachen sie direkt und
 * invalidieren die Listen, damit die neue Conversation sofort auftaucht.
 *
 * Bewusst schlanke, wiederverwendbare API (auch von ContactsPage nutzbar):
 *   const { startConversation } = useStartConversation();
 *   const conv = await startConversation({ contactId, channel: 'WHATSAPP' });
 *
 * Fehler werden als ApiError weitergereicht (z.B. 422 no_active_channel_account,
 * 404 contact_not_found, 403) — der Aufrufer entscheidet über die UI-Reaktion.
 */
export function useStartConversation() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const startConversation = useCallback(
    async (input: StartConversationInput): Promise<ConversationDetail> => {
      const conv = await api.post<ConversationDetail>('/api/conversations', {
        contactId: input.contactId,
        channel: input.channel,
        ...(input.team ? { team: input.team } : {}),
      });
      queryClient.setQueryData(['conversation', conv.id], conv);
      queryClient.invalidateQueries({ queryKey: ['conversations'] });
      navigate(`/inbox/${conv.id}`);
      return conv;
    },
    [navigate, queryClient],
  );

  return { startConversation };
}

/** Liefert eine menschenlesbare Fehlermeldung für die bekannten Fehlercodes. */
export function startConversationErrorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    const body = err.body as { error?: string } | undefined;
    switch (body?.error) {
      case 'contact_not_found':
        return 'Kontakt nicht gefunden.';
      case 'no_active_channel_account':
        return 'Kein aktives Konto für diesen Kanal eingerichtet.';
      case 'forbidden':
        return 'Keine Berechtigung für das Team dieser Conversation.';
      default:
        break;
    }
    if (err.status === 403) return 'Keine Berechtigung für das Team dieser Conversation.';
  }
  return err instanceof Error ? err.message : 'Unbekannter Fehler';
}
